<template>
  <div class="v3-custom-checkbox" :class="'type-' + inputType">
    <input
      :id="resolvedInputId"
      :checked="modelValue"
      v-bind="inputBindings"
      @change="onChange"
      @keydown.enter.prevent="onEnterKey"
      :type="inputType"
      :data-test-id="testId"
    />
    <div class="checkbox-toggler"><i class="ri-check-line check-icon"></i></div>
    <label
      v-if="hasVisibleLabel"
      :for="resolvedInputId"
      class="checkbox-label flex-1 ms-2"
      :class="{ 'checkbox-label-disabled': disabled }"
    >
      {{ trimmedLabel }}
    </label>
  </div>
</template>

<script>
import { computed } from "vue"

export default {
  props: {
    id: {
      type: String,
      default: ""
    },
    ariaLabelledby: {
      type: String,
      default: ""
    },
    name: {
      type: String,
      default: ""
    },
    label: {
      type: String,
      default: ""
    },
    /** When `label` is empty (visual label is separate), set this for screen readers. */
    inputAriaLabel: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: "checkbox"
    },
    testId: {
      type: String,
      default: ""
    },
    modelValue: Boolean
  },
  emits: ["change", "update:model-value"],
  setup(props, { emit }) {
    const trimmedLabel = computed(() => (props.label || "").trim())
    const trimmedInputAriaLabel = computed(() =>
      (props.inputAriaLabel || "").trim()
    )
    const trimmedAriaLabelledby = computed(() =>
      (props.ariaLabelledby || "").trim()
    )

    const hasVisibleLabel = computed(() => !!trimmedLabel.value)

    const fallbackId = `v3-custom-check-${Math.random().toString(36).slice(2, 11)}`
    const resolvedInputId = computed(() =>
      (props.id || "").trim() ? (props.id || "").trim() : fallbackId
    )

    const inputBindings = computed(() => {
      const out = {
        name: props.name,
        disabled: props.disabled
      }
      if (trimmedAriaLabelledby.value) {
        out["aria-labelledby"] = trimmedAriaLabelledby.value
        return out
      }
      if (!hasVisibleLabel.value && trimmedInputAriaLabel.value) {
        out["aria-label"] = trimmedInputAriaLabel.value
      }
      return out
    })

    const onChange = (event) => {
      emit("change", event.target.checked)
      emit("update:model-value", event.target.checked)
    }
    const inputType = computed(() => {
      return props.type == "checkbox" ? "checkbox" : "radio"
    })
    const onEnterKey = () => {
      if (props.disabled) return
      if (inputType.value === "checkbox") {
        const next = !props.modelValue
        emit("change", next)
        emit("update:model-value", next)
        return
      }
      if (!props.modelValue) {
        emit("change", true)
        emit("update:model-value", true)
      }
    }
    return {
      onChange,
      onEnterKey,
      inputType,
      trimmedLabel,
      hasVisibleLabel,
      resolvedInputId,
      inputBindings
    }
  }
}
</script>

<style scoped>
.checkbox-label-disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
